import { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../core/common/Breadcrumb/breadcrumb";
import Table from "../core/common/dataTable/index";
import { AppDispatch, RootState } from "../core/redux/store";
import {
  fetchClassroomById,
} from "../core/redux/classroomSlice";
import { updateClassroom } from "../core/redux/classroomSlice";
import { classroomService, ClassroomContentItem, PaginatedResponse } from "../core/services/classroom.service";
import { fetchAllCategories } from "../core/redux/categorySlice";

const ClassroomContents = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { classCode } = useParams();
  const { current, updating, error } = useSelector((state: RootState) => state.classrooms);

  const [loading, setLoading] = useState<boolean>(false);
  const [contents, setContents] = useState<ClassroomContentItem[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [total, setTotal] = useState<number>(0);
  const [search, setSearch] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("is_attached");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const { levels } = useSelector((state: RootState) => state.levels);
  const { categories } = useSelector((state: RootState) => state.categories);
  const [levelId, setLevelId] = useState<string>("");
  const [categoryId, setCategoryId] = useState<string>("");
  const [attachedState, setAttachedState] = useState<string>("all"); // all | attached | detached
  const [editLevelIds, setEditLevelIds] = useState<number[]>([]);
  const wasUpdating = useRef(false);

  useEffect(() => {
    if (!classCode) return;
    const id = Number(classCode);
    if (!Number.isFinite(id)) return;
    dispatch(fetchClassroomById({ id }));
  }, [classCode, dispatch]);

  const fetchContents = async () => {
    if (!classCode) return;
    const id = Number(classCode);
    if (!Number.isFinite(id)) return;
    setLoading(true);
    try {
      const res: PaginatedResponse<ClassroomContentItem> = await classroomService.contents(id, {
        page,
        per_page: pageSize,
        search: search || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
        level_id: levelId ? Number(levelId) : undefined,
        category_id: categoryId ? Number(categoryId) : undefined,
        is_attached: attachedState === "all" ? undefined : attachedState === "attached",
      });
      let data = res.data || [];
      // Default grouping: detached first, then title ascending within groups
      if (sortBy === "is_attached") {
        data = [...data].sort((a, b) => {
          const aAttached = a.is_attached ? 1 : 0;
          const bAttached = b.is_attached ? 1 : 0;
          if (aAttached !== bAttached) return aAttached - bAttached; // 0 (detached) first
          return (a.title || "").localeCompare(b.title || "");
        });
      }
      setContents(data);
      setTotal(res.meta?.total || 0);
    } catch (e) {
      // handled globally by utils.handleResponse
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classCode, page, pageSize, search, sortBy, sortDir, levelId, categoryId, attachedState]);

  // Close modal and refresh data after successful update
  useEffect(() => {
    if (wasUpdating.current && !updating) {
      if (!error) {
        (document.querySelector("#edit_levels_modal .btn-close") as HTMLButtonElement)?.click();
        fetchClassroomById && dispatch(fetchClassroomById({ id: Number(classCode) }));
        fetchContents();
      }
    }
    wasUpdating.current = updating;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updating, error]);

  useEffect(() => {
    // Load categories once
    dispatch(fetchAllCategories());
  }, [dispatch]);

  const rowSelection = useMemo(() => ({
    selectedRowKeys,
    onChange: (keys: React.Key[]) => setSelectedRowKeys(keys),
  }), [selectedRowKeys]);

  const handleAttachSelected = async () => {
    if (!classCode || selectedRowKeys.length === 0) return;
    const id = Number(classCode);
    try {
      await classroomService.attachContents(id, selectedRowKeys as number[]);
      setSelectedRowKeys([]);
      await fetchContents();
    } catch (error) {
      // error handled globally
    }
  };

  const handleDetachSelected = async () => {
    if (!classCode || selectedRowKeys.length === 0) return;
    const id = Number(classCode);
    try {
      await classroomService.detachContents(id, selectedRowKeys as number[]);
      setSelectedRowKeys([]);
      await fetchContents();
    } catch (error) {
      // error handled globally
    }
  };

  const onTableChange = (pagination: any, _filters: any, sorter: any) => {
    if (pagination) {
      if (pagination.current !== page) setPage(pagination.current);
      if (pagination.pageSize !== pageSize) setPageSize(pagination.pageSize);
    }
    if (sorter && sorter.field) {
      const sortFieldMap: Record<string, string> = {
        id: "id",
        title: "title",
        is_attached: "is_attached",
        level: "level",
        category: "category",
        topic: "topic",
        content_type: "content_type",
      };
      const fieldKey = sortFieldMap[sorter.field] || "id";
      setSortBy(fieldKey);
      setSortDir(sorter.order === "ascend" ? "asc" : "desc");
    }
  };

  const handleEditLevelChange = (levelId: number) => {
    setEditLevelIds((prev) =>
      prev.includes(levelId) ? prev.filter((id) => id !== levelId) : [...prev, levelId]
    );
  };

  const onOpenLevelsModal = () => {
    const initial = (current?.levels || []).map((l: any) => l.id);
    setEditLevelIds(initial);
  };

  const handleUpdateLevels = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!current?.id) return;
    wasUpdating.current = true;
    dispatch(updateClassroom({
      id: current.id,
      name: current.name,
      level_ids: editLevelIds.length > 0 ? editLevelIds : undefined,
    }));
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      sorter: true,
      render: (text: string) => (
        <span className="fw-medium">{text}</span>
      ),
    },
    {
      title: "Level",
      dataIndex: ["level", "name"],
      sorter: true,
      render: (_: any, record: ClassroomContentItem) => record.level?.name || "-",
    },
    {
      title: "Category",
      dataIndex: ["category", "name"],
      sorter: true,
      render: (_: any, record: ClassroomContentItem) => record.category?.name || "-",
    },
    {
      title: "Topic",
      dataIndex: ["topic", "name"],
      sorter: true,
      render: (_: any, record: ClassroomContentItem) => record.topic?.name || "-",
    },
    {
      title: "Type",
      dataIndex: ["content_type", "name"],
      sorter: true,
      render: (_: any, record: ClassroomContentItem) => record.content_type?.name || "-",
    },
    {
      title: "Attached",
      dataIndex: "is_attached",
      sorter: true,
      defaultSortOrder: sortBy === "is_attached" ? "ascend" : undefined,
      render: (value: boolean) => (
        <span className={`badge ${value ? "bg-success-transparent text-success" : "bg-danger-transparent text-danger"}`}>
          {value ? "Attached" : "Detached"}
        </span>
      )
    },
  ];

  return (
    <>
      <Breadcrumb
        title={current?.name || ""}
        subtitles={[
          {
            title: "Cohorts",
            link: "/student-groups/",
          },
        ]}
      />
      <div className="content">
        <div className="container">
          <div className="row">
            <div className="col-12 mb-3">
              <button
                className="btn btn-dark rounded-pill"
                type="button"
                onClick={() => {
                  if (typeof window !== "undefined") {
                    navigate("/student-groups/");
                  }
                }}
              >
                <i className="isax isax-arrow-left-2 me-2"></i>
                Back
              </button>
            </div>
            <div className="col-lg-12">
              <div className="page-title d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="fw-bold">Contents</h5>
                  <p className="text-muted mb-0">
                    {total} content{total !== 1 ? "s" : ""} available
                  </p>
                </div>
                <div className="d-flex align-items-center">
                  <div className="d-flex gap-2">
                    <button
                      className="btn btn-secondary rounded-pill"
                      onClick={handleAttachSelected}
                      disabled={selectedRowKeys.length === 0 || loading}
                    >
                      <i className="isax isax-link-2 me-2"></i>
                      Attach selected
                    </button>
                    <button
                      className="btn btn-outline-secondary rounded-pill"
                      onClick={handleDetachSelected}
                      disabled={selectedRowKeys.length === 0 || loading}
                    >
                      <i className="isax isax-unlink me-2"></i>
                      Detach selected
                    </button>
                  </div>
                </div>
              </div>
              {Array.isArray(current?.levels) && current?.levels.length === 0 && (
                <div className="alert alert-warning mt-3 d-flex align-items-center justify-content-between" role="alert">
                  <span>Attach level to Cohort to attach contents.</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-secondary rounded-pill"
                    data-bs-toggle="modal"
                    data-bs-target="#edit_levels_modal"
                    onClick={onOpenLevelsModal}
                  >
                    Attach Level
                  </button>
                </div>
              )}
              <div className="row g-3 align-items-end mb-3">
                <div className="col-md-3 mt-1">
                  <div className="input-icon">
                    <span className="input-icon-addon">
                      <i className="isax isax-search-normal-14" />
                    </span>
                    <input
                      type="search"
                      className="form-control form-control-md"
                      placeholder="Search"
                      value={search}
                      onChange={(e) => {
                        setPage(1);
                        setSearch(e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="col-md-3 mt-1">
                  <label className="form-label">Level</label>
                  <select
                    className="form-select"
                    value={levelId}
                    onChange={(e) => {
                      setPage(1);
                      setLevelId(e.target.value);
                    }}
                  >
                    <option value="">All</option>
                    {levels?.map((lvl: any) => (
                      <option key={lvl.value} value={lvl.value}>{lvl.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mt-1">
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={categoryId}
                    onChange={(e) => {
                      setPage(1);
                      setCategoryId(e.target.value);
                    }}
                  >
                    <option value="">All</option>
                    {categories?.map((cat: any) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-3 mt-1">
                  <label className="form-label">Attached</label>
                  <select
                    className="form-select"
                    value={attachedState}
                    onChange={(e) => {
                      setPage(1);
                      setAttachedState(e.target.value);
                    }}
                  >
                    <option value="all">All</option>
                    <option value="attached">Attached</option>
                    <option value="detached">Detached</option>
                  </select>
                </div>
              </div>
              <Table
                dataSource={contents}
                columns={columns as any}
                Search={false}
                rowKey={(record: ClassroomContentItem) => String(record.id)}
                rowSelection={rowSelection}
                onChange={onTableChange}
                pagination={{
                  current: page,
                  pageSize: pageSize,
                  total: total,
                  showSizeChanger: true,
                  pageSizeOptions: [25, 50, 100, 200],
                  locale: { items_per_page: "" },
                  nextIcon: (<span><i className="fas fa-angle-right" /></span>),
                  prevIcon: (<span><i className="fas fa-angle-left" /></span>),
                }}
                style={{ opacity: loading ? 0.6 : 1 }}
              />

              {/* Edit Levels Modal */}
              <div className="modal fade" id="edit_levels_modal" data-bs-backdrop="static" data-bs-keyboard="false">
                <div className="modal-dialog modal-dialog-centered modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="fw-bold">Attach Levels</h5>
                      <button
                        type="button"
                        className="btn-close custom-btn-close"
                        data-bs-dismiss="modal"
                        aria-label="Close"
                      >
                        <i className="isax isax-close-circle5" />
                      </button>
                    </div>
                    <form onSubmit={handleUpdateLevels}>
                      <div className="modal-body pb-0">
                        <div className="mb-3">
                          <label className="form-label">Levels</label>
                          <div className="d-flex flex-wrap gap-2">
                            {levels.map((level: any) => (
                              <div key={level.value} className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  value={level.value}
                                  id={`edit-level-${level.value}`}
                                  checked={editLevelIds.includes(parseInt(level.value))}
                                  onChange={() => handleEditLevelChange(parseInt(level.value))}
                                />
                                <label className="form-check-label" htmlFor={`edit-level-${level.value}`}>
                                  {level.name}
                                </label>
                              </div>
                            ))}
                          </div>
                          <span className="text-muted fs-12">
                            The contents of selected levels will be attached to this cohort automatically.
                          </span>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <button
                          className="btn bg-gray-100 rounded-pill me-2"
                          type="button"
                          data-bs-dismiss="modal"
                        >
                          Cancel
                        </button>
                        <button
                          className="btn btn-secondary rounded-pill"
                          type="submit"
                          disabled={updating || editLevelIds.length === 0}
                        >
                          {updating ? "Saving..." : "Save Changes"}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </>
  );
};

export default ClassroomContents;
